import React, { FC, useState } from 'react';

import { Stack, Box, Divider, HStack } from '@chakra-ui/react';
import { useSelector, useDispatch } from 'react-redux';
import { useDeepCompareEffect } from 'react-use';

import { selectCompliance } from 'app/containers/Compliance/selectors';
import { actions } from 'app/containers/Compliance/slice';
import { BorderedBox } from 'components/DataDisplay/Box/BorderedBox';
import { FilterLabelWrapper } from 'components/DataDisplay/Box/BorderedBox/FilterLabelWrapper';
import { severityOptionsWithAll } from 'components/DataDisplay/Utils/renderSeverity';
import {
  StackedHeader,
  Drawer,
  Select,
  ChakraMarkdown,
  DetailsIdentifier,
  ExpandableText,
} from 'components/index';
import { PoliciesTable } from 'containers/Compliance/Components/ComplianceTables/PoliciesTable';

interface Props {
  subControl: Record<string, any>;
  isOpen: boolean;
  onClose: () => void;
}

export const PoliciesUnderSubControlDrawer: FC<Props> = props => {
  const { isOpen, onClose, subControl } = props;
  const dispatch = useDispatch();
  const { rules, selectedBlueprint, environment } = useSelector(
    selectCompliance,
  );
  const [severity, setSeverity] = useState(severityOptionsWithAll[0]);

  useDeepCompareEffect(() => {
    if (selectedBlueprint?.id) {
      dispatch(
        actions.loadRules({
          q: {
            blueprintId: selectedBlueprint?.id,
            standardId: subControl?.id,
            page: rules.page.info.page_number,
            size: rules.page.info.page_size,
            severity: severity?.value?.toUpperCase(),
            environmentId: environment?.id,
          },
        }),
      );
    }
  }, [
    dispatch,
    selectedBlueprint,
    rules.page.info,
    subControl,
    environment,
    severity,
  ]);

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      header={
        <StackedHeader
          upper={subControl?.compliance?.replace('_', ' ')}
          lower={
            'Control: ' +
            subControl?.control +
            ' / ' +
            'Sub Control: ' +
            subControl?.sub_control
          }
          type={subControl?.compliance}
          iconFilled={false}
        />
      }
      styles={{
        content: { maxW: '94rem' },
        // The resource metadata drawer can open on top of this drawer from a
        // portal outside it; keep this drawer from locking scroll or trapping
        // focus so the metadata drawer's tables scroll and its selects open.
        drawer: { blockScrollOnMount: false, trapFocus: false },
      }}
      closeButton
      body={
        <Stack h="full" w="full" spacing={5}>
          <HStack spacing={10} align="start">
            <Box w="67%">
              <DetailsIdentifier
                label="Sub-Control Description"
                value={
                  <ExpandableText noOfLines={3}>
                    <ChakraMarkdown
                      content={subControl?.sub_control_description || '-'}
                    />
                  </ExpandableText>
                }
                isInline={false}
                spacing={2}
              />
            </Box>
            <Divider
              orientation="vertical"
              alignSelf="stretch"
              borderColor="componentBorder"
            />
            <Stack>
              <DetailsIdentifier label="Cloud" />
              <DetailsIdentifier
                label="Sub-Control Policies"
                value={subControl?.rules?.total}
                align="center"
              />
              <DetailsIdentifier
                label="Exceptions"
                value={subControl?.rules?.total - subControl?.rules?.selected}
              />
            </Stack>
          </HStack>
          <BorderedBox h="full" p={4}>
            <Stack spacing={5} h="full">
              <HStack w="full">
                <FilterLabelWrapper>FILTER BY</FilterLabelWrapper>
                <Box>
                  <Select
                    options={severityOptionsWithAll}
                    value={severity}
                    onChange={setSeverity}
                    showIconInValueContainer
                    menuPortalTarget={document.body}
                    isAFilter
                    leftMessage="Severity"
                    showUndefinedValueLabel={false}
                  />
                </Box>
              </HStack>
              <Box flex={1} h="full">
                <PoliciesTable subControlRecord={subControl} />
              </Box>
            </Stack>
          </BorderedBox>
        </Stack>
      }
    />
  );
};
